import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { useLiveOverview } from './LiveOverviewContext';
import { useDegradedPolling, useLiveEvent } from '../hooks/useLiveEvents';
import notificationService, {
  type NotificationReadEvent,
  type StaffNotification,
} from '../services/notificationService';
import type { WaitingContact } from '../services/liveOverviewService';
import type { ChimeTone } from '@/lib/chime';
import type { OpenTarget } from '@/lib/chimeScheduler';
import {
  EMPTY_NOTIFICATION_LIST,
  applyRead,
  fromResponse,
  hasUrgentUnread,
  isUnread,
  notificationExternalUrl,
  notificationUrl,
  openExternalUrl,
  revertRead,
  unreadIdsFor,
  unreadTotal,
  upsertNotification,
  withServerUnread,
  type NotificationListState,
  type ReadTarget,
} from '@/lib/notificationList';
import { setUnreadInTitle } from '@/lib/documentTitle';
import {
  DEFAULT_NOTIFICATION_PREFS,
  loadNotificationPrefs,
  saveNotificationPrefs,
  subscribeNotificationPrefs,
  type NotificationPrefs,
} from '@/lib/notificationPrefs';
import {
  browserDesktopNotifier,
  createNotificationSound,
  type DesktopPermission,
  type NotificationSound,
} from '@/lib/notificationSound';
import { serverNow } from '@/lib/serverClock';
import { withDeadline } from '@/lib/requestDeadline';

/**
 * The bell's data, sound and tab agreement (contract livecenter §5/§7).
 *
 * Lives above the router next to LiveEventsProvider, so a chime keeps
 * repeating while the person navigates. Loads `GET /api/notifications` once
 * logged in, applies `notification.upsert` / `notification.read` live,
 * reloads on `ready`, polls while degraded, and prefixes the tab title with
 * „(n) “ while something is unread.
 */

export interface NotificationAlert {
  id: number;
  title: string;
  /** Increments per alert, so the same entry growing again still re-triggers the wiggle. */
  seq: number;
}

export interface NotificationsContextValue {
  items: StaffNotification[];
  unreadCount: number;
  hasUrgentUnread: boolean;
  loading: boolean;
  /** Newest urgent alert (new or grown) — drives the bell's wiggle and its live region. */
  latestAlert: NotificationAlert | null;
  markRead: (target: ReadTarget) => Promise<void>;
  prefs: NotificationPrefs;
  updatePrefs: (patch: Partial<NotificationPrefs>) => void;
  /** A chime was due but the browser's autoplay lock swallowed it. */
  soundBlocked: boolean;
  enableSound: () => Promise<void>;
  previewChime: (tone: ChimeTone) => void;
  desktopPermission: DesktopPermission;
  requestDesktop: () => Promise<boolean>;
  /** The chat/call Verläufe shows right now (null = none): stops its alarms, marks it read. */
  noteChatOpened: (target: OpenTarget | null) => void;
  /** The router's navigate, registered from inside the router (desktop-notification clicks). */
  registerNavigator: (navigate: (url: string) => void) => () => void;
}

const noop = () => {};

export const NOTIFICATIONS_FALLBACK: NotificationsContextValue = {
  items: [],
  unreadCount: 0,
  hasUrgentUnread: false,
  loading: false,
  latestAlert: null,
  markRead: async () => {},
  prefs: DEFAULT_NOTIFICATION_PREFS,
  updatePrefs: noop,
  soundBlocked: false,
  enableSound: async () => {},
  previewChime: noop,
  desktopPermission: 'unsupported',
  requestDesktop: async () => false,
  noteChatOpened: noop,
  registerNavigator: () => noop,
};

export const NOTIFICATIONS_REFETCH_THROTTLE_MS = 1_000;

const NotificationsContext = createContext<NotificationsContextValue>(NOTIFICATIONS_FALLBACK);

type ListUpdater = (state: NotificationListState) => NotificationListState;

/**
 * Stable identity of the open callback requests (null = none), e.g.
 * `callback:1k3x9`. Every tab derives the same key from the same data.
 */
export function openCallbacksKey(waiting: WaitingContact[], items: StaffNotification[]): string | null {
  const parts = [
    ...waiting.filter((w) => w.state === 'callback').map((w) => `w:${w.contact_key}@${w.waiting_since}`),
    ...items.filter((i) => i.kind === 'callback_requested' && !i.resolved_at).map((i) => `n:${i.id}`),
  ].sort();
  if (parts.length === 0) return null;
  let hash = 5381;
  for (const ch of parts.join('|')) hash = ((hash * 33) ^ ch.charCodeAt(0)) >>> 0;
  return `callback:${hash.toString(36)}`;
}

const signature = (item: StaffNotification | undefined) => (item ? `${item.updated_at}|${item.count}` : '');
const targetKey = (target: OpenTarget | null) => (target ? `${target.contactKey ?? ''}|${target.callSid ?? ''}` : '');

export const NotificationsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { overview, loading: overviewLoading } = useLiveOverview();
  // Desktop-notification clicks run outside render: they read the newest tickets here.
  const ticketsRef = useRef(overview.callback_tickets);
  ticketsRef.current = overview.callback_tickets;

  const [list, setList] = useState<NotificationListState>(EMPTY_NOTIFICATION_LIST);
  const [loading, setLoading] = useState(isAuthenticated);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [latestAlert, setLatestAlert] = useState<NotificationAlert | null>(null);
  const [prefs, setPrefs] = useState<NotificationPrefs>(loadNotificationPrefs);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const [desktopPermission, setDesktopPermission] = useState<DesktopPermission>(browserDesktopNotifier.permission);

  const listRef = useRef(list);
  const prefsRef = useRef(prefs);
  const authedRef = useRef(isAuthenticated);
  const generationRef = useRef(0);
  const inFlightRef = useRef(false);
  const dirtyRef = useRef(false);
  const pendingUpdatesRef = useRef<ListUpdater[]>([]);
  /** Our optimistic reads still on their way to the server — kept across a refetch. */
  const pendingReadsRef = useRef(new Map<string, ReadTarget>());
  const primedRef = useRef(false);
  const lastStartRef = useRef(0);
  const throttleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alertSeqRef = useRef(0);
  const lastStampRef = useRef(0);
  const openTargetRef = useRef<OpenTarget | null>(null);
  const startupCheckedRef = useRef(false);
  const navigatorRef = useRef<((url: string) => void) | null>(null);
  const soundRef = useRef<NotificationSound | null>(null);
  const markReadRef = useRef<(target: ReadTarget) => Promise<void>>(async () => {});

  const commit = useCallback((next: NotificationListState) => {
    if (next === listRef.current) return;
    listRef.current = next;
    setList(next);
  }, []);

  const updatePrefs = useCallback((patch: Partial<NotificationPrefs>) => {
    const next = { ...prefsRef.current, ...patch };
    prefsRef.current = next;
    saveNotificationPrefs(next);
    setPrefs(next);
  }, []);

  useEffect(
    () =>
      subscribeNotificationPrefs((next) => {
        prefsRef.current = next;
        setPrefs(next);
      }),
    [],
  );

  const openUrl = useCallback((url: string) => {
    if (navigatorRef.current) navigatorRef.current(url);
    else window.location.assign(url);
  }, []);

  useEffect(() => {
    const sound = createNotificationSound({
      getPrefs: () => prefsRef.current,
      onBlockedChange: setSoundBlocked,
      now: () => serverNow(),
      onDesktopClick: (item) => {
        window.focus();
        const external = notificationExternalUrl(item);
        const url = notificationUrl(item, ticketsRef.current);
        if (external) openExternalUrl(external);
        else if (url) openUrl(url);
        void markReadRef.current([item.id]);
      },
    });
    soundRef.current = sound;
    return () => {
      sound.destroy();
      soundRef.current = null;
    };
  }, [openUrl]);

  const scheduleRef = useRef<() => void>(noop);
  const applyOpenTargetRef = useRef<() => void>(noop);

  const load = useCallback(async () => {
    if (!authedRef.current) return;
    if (inFlightRef.current) {
      dirtyRef.current = true;
      return;
    }
    const generation = generationRef.current;
    inFlightRef.current = true;
    dirtyRef.current = false;
    pendingUpdatesRef.current = [];
    lastStartRef.current = Date.now();
    try {
      const response = await withDeadline(notificationService.list());
      if (generation !== generationRef.current) return;
      let next = fromResponse(response);
      pendingReadsRef.current.forEach((target, stamp) => {
        next = applyRead(next, target, stamp);
      });
      next = pendingUpdatesRef.current.reduce((acc, update) => update(acc), next);
      const scheduler = soundRef.current?.scheduler;
      if (!primedRef.current) {
        primedRef.current = true;
        scheduler?.prime(next.items);
      } else {
        next.items.forEach((item) => scheduler?.observe(item, 'snapshot'));
      }
      commit(next);
      setLoadedOnce(true);
      // A reload can bring entries of the chat that is open right now.
      applyOpenTargetRef.current();
    } catch (error) {
      if (generation !== generationRef.current) return;
      console.warn('Notifications could not be loaded:', error);
    } finally {
      if (generation === generationRef.current) {
        inFlightRef.current = false;
        pendingUpdatesRef.current = [];
        setLoading(false);
        if (dirtyRef.current) scheduleRef.current();
      }
    }
  }, [commit]);

  const scheduleRefetch = useCallback(() => {
    if (!authedRef.current || throttleRef.current) return;
    const wait = Math.max(0, lastStartRef.current + NOTIFICATIONS_REFETCH_THROTTLE_MS - Date.now());
    throttleRef.current = setTimeout(() => {
      throttleRef.current = null;
      void load();
    }, wait);
  }, [load]);
  scheduleRef.current = scheduleRefetch;

  useEffect(() => {
    authedRef.current = isAuthenticated;
    generationRef.current += 1;
    inFlightRef.current = false;
    dirtyRef.current = false;
    pendingUpdatesRef.current = [];
    pendingReadsRef.current.clear();
    primedRef.current = false;
    startupCheckedRef.current = false;
    openTargetRef.current = null;
    lastStartRef.current = 0;
    if (throttleRef.current) clearTimeout(throttleRef.current);
    throttleRef.current = null;
    soundRef.current?.scheduler.reset();
    commit(EMPTY_NOTIFICATION_LIST);
    setLoadedOnce(false);
    setLatestAlert(null);
    setLoading(isAuthenticated);
    if (isAuthenticated) void load();
    return () => {
      if (throttleRef.current) clearTimeout(throttleRef.current);
      throttleRef.current = null;
    };
  }, [isAuthenticated, load, commit]);

  const applyUpdate = useCallback(
    (update: ListUpdater) => {
      if (inFlightRef.current) pendingUpdatesRef.current.push(update);
      commit(update(listRef.current));
    },
    [commit],
  );

  const visibleOpenTarget = (): OpenTarget | null =>
    document.visibilityState === 'visible' ? openTargetRef.current : null;

  const nextStamp = () => {
    // Unique per optimistic read, so a rollback only undoes its own entries.
    lastStampRef.current = Math.max(serverNow(), lastStampRef.current + 1);
    return new Date(lastStampRef.current).toISOString();
  };

  const markRead = useCallback(
    async (target: ReadTarget) => {
      if (!authedRef.current || (Array.isArray(target) && target.length === 0)) return;
      const generation = generationRef.current;
      const stamp = nextStamp();
      const outsideBefore = listRef.current.outsideUnread;
      // A reload in flight re-applies buffered updates on its answer — but
      // never this read once it was rolled back.
      let reverted = false;
      pendingReadsRef.current.set(stamp, target);
      applyUpdate((state) => (reverted ? state : applyRead(state, target, stamp)));
      soundRef.current?.scheduler.markRead(target);
      try {
        const response = await notificationService.markRead(target === 'all' ? { all: true } : { ids: target });
        if (generation !== generationRef.current) return;
        if (typeof response?.unread_count === 'number') {
          commit(withServerUnread(listRef.current, response.unread_count));
        }
      } catch (error) {
        if (generation !== generationRef.current) return;
        reverted = true;
        pendingReadsRef.current.delete(stamp);
        commit(revertRead(listRef.current, target, stamp, outsideBefore));
        // Still unread on the server: a waiting customer must not go silent.
        const ids = target === 'all' ? null : new Set(target);
        soundRef.current?.scheduler.rearm(listRef.current.items.filter((i) => !ids || ids.has(i.id)));
        toast.error('Konnte nicht als gelesen markiert werden. Bitte versuche es erneut.');
      } finally {
        pendingReadsRef.current.delete(stamp);
      }
    },
    [applyUpdate, commit],
  );
  markReadRef.current = markRead;

  useLiveEvent<StaffNotification>('notification.upsert', (item) => {
    if (!authedRef.current || !item || typeof item.id !== 'number') return;
    const previous = listRef.current.items.find((i) => i.id === item.id);
    applyUpdate((state) => upsertNotification(state, item));
    soundRef.current?.scheduler.observe(item, 'live');
    const isOpenAlert = item.urgent && isUnread(item) && !item.resolved_at;
    if (isOpenAlert && signature(previous) !== signature(item)) {
      alertSeqRef.current += 1;
      setLatestAlert({ id: item.id, title: item.title, seq: alertSeqRef.current });
    }
    // The person is looking at exactly this chat: it is read.
    const open = visibleOpenTarget();
    if (isUnread(item) && open && unreadIdsFor({ items: [item], outsideUnread: 0 }, open).length > 0) {
      void markRead([item.id]);
    }
  });

  useLiveEvent<NotificationReadEvent>('notification.read', (event) => {
    if (!authedRef.current || !event) return;
    const target: ReadTarget | null = event.all ? 'all' : Array.isArray(event.ids) ? event.ids : null;
    if (!target) return;
    const stamp = nextStamp();
    applyUpdate((state) => applyRead(state, target, stamp));
    soundRef.current?.scheduler.markRead(target);
  });

  useLiveEvent('ready', () => scheduleRefetch());
  useDegradedPolling(() => void load());

  // „Wartet auf Rückmeldung“ rings once when the app opens (plan §4).
  useEffect(() => {
    if (!isAuthenticated || !loadedOnce || overviewLoading || startupCheckedRef.current) return;
    startupCheckedRef.current = true;
    const key = openCallbacksKey(overview.waiting, list.items);
    // Keyed by WHICH callbacks are open — identical in every tab, independent of
    // clocks and minute boundaries: tabs restored together ring once, not each.
    if (key) soundRef.current?.scheduler.ringStartup(key);
  }, [isAuthenticated, loadedOnce, overviewLoading, overview.waiting, list.items]);

  const unreadCount = unreadTotal(list);

  // Through the one title writer: the page title (e.g. Verläufe) stays and
  // only the "(n) " prefix follows the count.
  useEffect(() => {
    setUnreadInTitle(unreadCount);
  }, [unreadCount]);
  useEffect(() => () => setUnreadInTitle(0), []);

  /**
   * Only a chat the person can actually see counts as open: a background
   * tab parked on it must neither silence the alarm nor mark it read.
   * Re-evaluated when the tab becomes visible and after every reload.
   */
  const applyOpenTarget = useCallback(() => {
    const target = visibleOpenTarget();
    soundRef.current?.scheduler.noteOpened(target);
    if (!target) return;
    const ids = unreadIdsFor(listRef.current, target);
    if (ids.length > 0) void markReadRef.current(ids);
  }, []);
  applyOpenTargetRef.current = applyOpenTarget;

  useEffect(() => {
    document.addEventListener('visibilitychange', applyOpenTarget);
    return () => document.removeEventListener('visibilitychange', applyOpenTarget);
  }, [applyOpenTarget]);

  const noteChatOpened = useCallback(
    (target: OpenTarget | null) => {
      if (targetKey(target) === targetKey(openTargetRef.current)) return;
      openTargetRef.current = target;
      applyOpenTarget();
    },
    [applyOpenTarget],
  );

  const registerNavigator = useCallback((navigate: (url: string) => void) => {
    navigatorRef.current = navigate;
    return () => {
      if (navigatorRef.current === navigate) navigatorRef.current = null;
    };
  }, []);

  const enableSound = useCallback(async () => {
    await soundRef.current?.enable();
  }, []);

  const previewChime = useCallback((tone: ChimeTone) => soundRef.current?.preview(tone), []);

  const requestDesktop = useCallback(async () => {
    const permission = await browserDesktopNotifier.request();
    setDesktopPermission(permission);
    const granted = permission === 'granted';
    updatePrefs({ desktopEnabled: granted });
    return granted;
  }, [updatePrefs]);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      items: list.items,
      unreadCount,
      hasUrgentUnread: hasUrgentUnread(list),
      loading: isAuthenticated && loading,
      latestAlert,
      markRead,
      prefs,
      updatePrefs,
      soundBlocked,
      enableSound,
      previewChime,
      desktopPermission,
      requestDesktop,
      noteChatOpened,
      registerNavigator,
    }),
    [
      list,
      unreadCount,
      isAuthenticated,
      loading,
      latestAlert,
      markRead,
      prefs,
      updatePrefs,
      soundBlocked,
      enableSound,
      previewChime,
      desktopPermission,
      requestDesktop,
      noteChatOpened,
      registerNavigator,
    ],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export function useNotifications(): NotificationsContextValue {
  return useContext(NotificationsContext);
}

export default NotificationsContext;
